import React, { useState, useEffect, useCallback } from 'react';
import {
  CalendarMonth,
  KeyboardDoubleArrowRight,
  Archive,
  EventBusyOutlined,
} from '@mui/icons-material';
import comms from '../comms';
import TripModal from '../TripModal';

// ── Scope tabs (top row) ─────────────────────────────────────────────────────
const SCOPES = [
  { id: 'schedule', label: 'Schedule', Icon: CalendarMonth },
  { id: 'upcoming', label: 'Upcoming', Icon: KeyboardDoubleArrowRight },
  { id: 'completed', label: 'Completed', Icon: Archive },
];

// Trip statuses considered "active" (job in progress) for the ACTIVE sub-tab.
const ACTIVE_KEYS = [
  'inprogress', 'progress', 'enroute', 'arrived', 'pickedup',
  'onboard', 'started', 'active', 'pickup', 'dropoff',
];

// Itinerary step styling (used by the route-itinerary view).
const stepStyles = {
  start: { label: 'Start', color: '#16a34a' },
  end: { label: 'End', color: '#dc2626' },
  break: { label: 'Break', color: '#ca8a04' },
  hub: { label: 'Hub', color: '#9333ea' },
  pickup: { label: 'Pickup', color: '#2563eb' },
  delivery: { label: 'Dropoff', color: '#ea580c' },
};

function getStyle(type) {
  if (!type) return stepStyles.pickup;
  const key = type.toLowerCase();
  if (key.includes('break')) return stepStyles.break;
  return stepStyles[key] || stepStyles.pickup;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function formatDateForAPI(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
}

function formatTime(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function dayLabel(date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  const prefix = diff === 0 ? 'Today' : diff === 1 ? 'Tomorrow' : d.toLocaleDateString('en-US', { weekday: 'long' });
  return `${prefix} ${String(d.getDate()).padStart(2, '0')} ${d.toLocaleDateString('en-US', { month: 'short' })}`;
}

function statusLabel(status) {
  if (!status) return '';
  return String(status).replace('Driver', '').replace('Vehicle', '').trim();
}

const addr = (t, lineKey, l1, l2) => t[lineKey] || `${t[l1] || ''} ${t[l2] || ''}`.trim();

// Normalize a History row into the trip shape the card/modal expect.
function normalizeHistory(t, i) {
  return {
    Id: t.Id || t.id || `H-${i}`,
    TripNo: t.TripNo || t.tripNo || t.Id || t.id || '',
    PassengerName: t.PassengerName || t.passengerName || '',
    OriginScheduledDate: t.OriginScheduledDateTime || t.OriginScheduledDate || t.date || t.Date || '',
    DestinationScheduledDate: t.DestinationScheduledDateTime || t.DestinationScheduledDate || '',
    Origin_AddressLine1: t.Origin || t.origin || t.Origin_AddressLine1 || '',
    Destination_AddressLine1: t.Destination || t.destination || t.Destination_AddressLine1 || '',
    Fare: t.Fare ?? t.fare ?? t.Amount ?? t.amount,
    AmbulatoryRequired: t.AmbulatoryRequired ?? 0,
    WheelchairRequired: t.WheelchairRequired ?? 0,
    TripStatus: t.TripStatus || 'Completed',
  };
}

function Future({ fleetSettings, navigateTo } = {}) {
  const [scope, setScope] = useState('schedule');
  const [statusTab, setStatusTab] = useState('scheduled');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [schedule, setSchedule] = useState(null);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);

  // 7-day options for the Schedule date picker.
  const dateOptions = [];
  {
    const today = new Date();
    for (let i = 0; i < 7; i++) {
      const date = addDays(today, i);
      dateOptions.push({
        date,
        displayName: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : date.toLocaleDateString('en-US', { weekday: 'long' }),
        fullDate: date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }),
      });
    }
  }

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const userSettings = JSON.parse(localStorage.getItem('userSettings'));
      const driverId = userSettings?.Id;
      if (!driverId) throw new Error('User not found');

      if (scope === 'completed') {
        const res = await comms.getDriverHistory(driverId);
        if (res && res.status === false) {
          setSchedule(null);
          setTrips([]);
          setError(res.message || null);
          return;
        }
        const raw = res?.trips || res?.history || res?.result || res?.data || [];
        setSchedule(null);
        setTrips((Array.isArray(raw) ? raw : []).map(normalizeHistory));
      } else if (scope === 'upcoming') {
        // Aggregate the next 6 days (tomorrow → +6) into one upcoming list.
        const dates = [];
        for (let i = 1; i <= 6; i++) dates.push(addDays(new Date(), i));
        const results = await Promise.all(
          dates.map((d) => comms.getDriverSchedule(driverId, formatDateForAPI(d)).catch(() => null))
        );
        const all = [];
        results.forEach((r) => {
          if (r?.status && Array.isArray(r.trips)) all.push(...r.trips);
        });
        all.sort((a, b) => new Date(a.OriginScheduledDate) - new Date(b.OriginScheduledDate));
        setSchedule(null);
        setTrips(all);
      } else {
        // 'schedule' scope — the date the user picked.
        const date = selectedDate;
        const res = await comms.getDriverSchedule(driverId, formatDateForAPI(date));
        if (res?.status) {
          setSchedule(res.schedule || null);
          setTrips(res.trips || []);
        } else {
          setSchedule(null);
          setTrips([]);
          setError(res?.message || 'Failed to fetch schedule');
        }
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching bookings');
      setSchedule(null);
      setTrips([]);
    } finally {
      setLoading(false);
    }
  }, [scope, selectedDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset to the SCHEDULED sub-tab when leaving a scope where ACTIVE applies.
  useEffect(() => {
    if (scope === 'completed') setStatusTab('scheduled');
  }, [scope]);

  const isActiveTrip = (t) => {
    const s = (t.TripStatus || '').toLowerCase().replace(/[\s_-]/g, '');
    return ACTIVE_KEYS.some((k) => s.includes(k));
  };

  const hasSchedule = scope === 'schedule' && schedule && schedule.itinerary && schedule.itinerary.length > 0;

  // SCHEDULED / ACTIVE filtering only applies to the Schedule scope.
  // Upcoming and Completed show all of their trips.
  const visibleTrips =
    scope === 'schedule'
      ? trips.filter((t) => (statusTab === 'active' ? isActiveTrip(t) : !isActiveTrip(t)))
      : trips;


  // Context label under the tabs.
  let contextLabel;
  if (scope === 'schedule') contextLabel = dayLabel(selectedDate);
  else if (scope === 'upcoming') contextLabel = 'Upcoming · next 6 days';
  else contextLabel = 'Completed trips';

  // Context-aware empty-state message.
  let emptyMessage;
  if (scope === 'completed') emptyMessage = 'You have no completed trips yet.';
  else if (statusTab === 'active') emptyMessage = 'No active trips right now.';
  else if (scope === 'upcoming') emptyMessage = 'No upcoming trips in the next 6 days.';
  else emptyMessage = 'No trips scheduled for this day.';

  return (
    <div>
      {/* Scope tabs */}
        <div className="mb-3 grid grid-cols-3 gap-2">
          {SCOPES.map((s) => {
            const Icon = s.Icon;
            const active = scope === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setScope(s.id)}
                className={`flex flex-col items-center gap-1 rounded-xl border px-1 py-2 text-xs font-semibold transition-colors ${
                  active
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-500'
                }`}
              >
                <Icon style={{ fontSize: 22 }} />
                {s.label}
              </button>
            );
          })}
        </div>

        {/* Schedule date picker (Schedule scope only) */}
        {scope === 'schedule' && (
          <div className="mb-3">
            <select
              value={selectedDate.toDateString()}
              onChange={(e) => setSelectedDate(new Date(e.target.value))}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {dateOptions.map((option, index) => (
                <option key={index} value={option.date.toDateString()}>
                  {option.displayName} - {option.fullDate}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* SCHEDULED / ACTIVE sub-tabs (Schedule scope only) */}
        {scope === 'schedule' && (
          <div className="mb-3 flex rounded-lg bg-gray-100 p-1">
            {['scheduled', 'active'].map((t) => (
              <button
                key={t}
                onClick={() => setStatusTab(t)}
                className={`flex-1 rounded-md py-2 text-xs font-bold uppercase tracking-wide transition-colors ${
                  statusTab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        )}

        {/* Context label */}
        <div className="mb-3 border-b border-gray-200 pb-2 text-base font-bold text-gray-900">
          {contextLabel}
        </div>

        {/* Loading */}
        {loading && (
          <div className="rounded-lg bg-white p-8 text-center shadow">
            <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500" />
            <p className="text-gray-600">Loading bookings...</p>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Content */}
        {!loading && !error && (
          <>
            {hasSchedule ? (
              /* ===== ITINERARY VIEW ===== */
              <div>
                <div className="mb-3 flex items-center justify-between rounded-lg bg-white px-3 py-2 shadow">
                  <span className="font-semibold text-gray-900">{schedule.routeName}</span>
                  <span className="text-sm text-gray-600">
                    {formatTime(schedule.startDateTime)} - {formatTime(schedule.endDateTime)}
                  </span>
                </div>
                <table className="min-w-full overflow-hidden rounded-lg bg-white text-sm shadow">
                  <thead>
                    <tr className="bg-gray-100 text-left text-xs font-medium uppercase text-gray-500">
                      <th className="w-20 px-2 py-1.5">Time</th>
                      <th className="w-16 px-2 py-1.5">Type</th>
                      <th className="px-2 py-1.5">Location</th>
                      <th className="px-2 py-1.5">Passenger</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.itinerary.map((step, index) => {
                      const s = getStyle(step.type);
                      const isTrip = step.type === 'pickup' || step.type === 'delivery';
                      return (
                        <tr key={step.stepId || index} className="border-t border-gray-100">
                          <td className="whitespace-nowrap px-2 py-1.5 font-medium text-gray-800">
                            {formatTime(step.time)}
                          </td>
                          <td className="whitespace-nowrap px-2 py-1.5">
                            <span
                              className="rounded px-1.5 py-0.5 text-xs font-semibold"
                              style={{ color: s.color, backgroundColor: s.color + '18' }}
                            >
                              {s.label}
                            </span>
                          </td>
                          <td className="max-w-0 truncate px-2 py-1.5 text-gray-700">{step.locationName}</td>
                          <td className="max-w-0 truncate px-2 py-1.5 text-gray-500">
                            {isTrip ? step.passengerName || '' : ''}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : visibleTrips.length > 0 ? (
              /* ===== TRIP CARD LIST ===== */
              <div className="space-y-3">
                {visibleTrips.map((trip, index) => {
                  const pickup = addr(trip, 'startAddress', 'Origin_AddressLine1', 'Origin_AddressLine2');
                  const dropoff = addr(trip, 'endAddress', 'Destination_AddressLine1', 'Destination_AddressLine2');
                  const fare = trip.Fare ?? trip.Amount;
                  return (
                    <button
                      key={trip.Id || index}
                      onClick={() => setSelectedTrip(trip)}
                      className="block w-full rounded-2xl bg-white p-3 text-left shadow-sm transition-transform active:scale-[0.99]"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-baseline gap-2">
                          <span className="text-sm font-semibold text-gray-900">#{trip.TripNo || '—'}</span>
                          {formatDate(trip.OriginScheduledDate) && (
                            <span className="text-xs font-medium text-gray-400">
                              {formatDate(trip.OriginScheduledDate)}
                            </span>
                          )}
                        </div>
                        <span
                          className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                            isActiveTrip(trip)
                              ? 'bg-blue-600 text-white'
                              : scope === 'completed'
                              ? 'bg-gray-100 text-gray-600'
                              : 'bg-blue-50 text-blue-700'
                          }`}
                        >
                          {statusLabel(trip.TripStatus) || 'Scheduled'}
                        </span>
                      </div>
                      {trip.PassengerName && (
                        <div className="mt-1 truncate text-sm font-medium text-gray-800">{trip.PassengerName}</div>
                      )}
                      <div className="mt-2 flex items-start gap-2 text-xs text-gray-600">
                        <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-blue-600" />
                        <span className="flex-1">
                          <span className="font-medium text-gray-800">{formatTime(trip.OriginScheduledDate)}</span>
                          {pickup && <span className="text-gray-500"> · {pickup}</span>}
                        </span>
                      </div>
                      <div className="mt-1 flex items-start gap-2 text-xs text-gray-600">
                        <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-red-500" />
                        <span className="flex-1">
                          <span className="font-medium text-gray-800">{formatTime(trip.DestinationScheduledDate)}</span>
                          {dropoff && <span className="text-gray-500"> · {dropoff}</span>}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center justify-between text-[11px] text-gray-500">
                        <span>
                          Amb {trip.AmbulatoryRequired ?? 0} · WC {trip.WheelchairRequired ?? 0}
                        </span>
                        {fare != null && fare !== '' && (
                          <span className="font-semibold text-gray-700">${Number(fare).toFixed(2)}</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              /* ===== EMPTY STATE ===== */
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <EventBusyOutlined style={{ fontSize: 56 }} className="text-gray-300" />
                <h3 className="mt-3 text-base font-semibold text-gray-800">No bookings found</h3>
                <p className="mt-1 max-w-xs text-sm text-gray-500">{emptyMessage}</p>
                <button
                  onClick={() => (navigateTo ? navigateTo('dispatch') : loadData())}
                  className="mt-4 rounded-lg bg-blue-700 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-800"
                >
                  {navigateTo ? 'See available jobs' : 'Refresh'}
                </button>
              </div>
            )}
          </>
        )}

      <TripModal
        trip={selectedTrip}
        onClose={() => setSelectedTrip(null)}
        fleetSettings={fleetSettings}
        onSave={(updated) => {
          setTrips((prev) => prev.map((t) => (t.Id === updated.Id ? { ...t, ...updated } : t)));
          setSelectedTrip((cur) => (cur ? { ...cur, ...updated } : cur));
        }}
      />
    </div>
  );
}

export default Future;
